import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { FloatingInput } from "@/components/ui/floating-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ChevronUp, ChevronDown, Check, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { isHandledError } from "@/lib/api/errors";
import { MATERIALITIES, getMaterialityId } from "@/data/materialsAndCategories";
import {
  useCategoriesData,
  useCreateBook,
  useUpdateBook,
  useCreateBookVersion,
  useUpdateBookVersion,
  useCreateBookCategories,
  useUpdateBookCategory,
  useDeleteBookCategory,
} from "@/hooks/api";
import type {
  BookVersionResponse,
  BookCategoriesResponse,
  BookCategoryItem,
} from "@/services/books.service";

interface CategoryConfig {
  csat: string;
  withoutNote: boolean;
  isMain: boolean;
}

interface BookFormData {
  bookId: string;
  /** ID da versão em `GET /book-versions/{bookId}`; ausente ⇒ POST ao salvar */
  bookVersionId?: number;
  title: string;
  isbn: string;
  sankhyaCode: string;
  materialidade: string;
  cartonadoSubtype: string;
  categories: Array<{
    id: string;
    categoryCode: string;
    /** ID da relação em `GET /book-categories/{bookId}`; ausente ⇒ incluído no POST */
    bookCategoryId?: number;
    csat: string;
    withoutNote: boolean;
    isMain: boolean;
  }>;
  cost: number;
  contractType: string;
  contractExpiryDate: string;
  contractStatus: string;
}

interface SectionStatus {
  identificacao: boolean;
  categorias: boolean;
  versoes: boolean;
}

interface SectionExpanded {
  identificacao: boolean;
  categorias: boolean;
  versoes: boolean;
}

interface BookCreateProps {
  headerTitle?: string;
  prefill?: Partial<BookFormData>;
  openSection?: keyof SectionExpanded;
}

function parseBRDate(dateStr: string): string | null {
  if (!dateStr || dateStr.length < 8) return null;
  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;
  const [day, month, year] = parts;
  if (!day || !month || !year || year.length !== 4) return null;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function extractBookVersionIdFromResponse(
  res: BookVersionResponse,
): number | undefined {
  const d = res.data;
  if (d == null) return undefined;
  if (Array.isArray(d)) {
    const first = d[0];
    return first?.id != null ? Number(first.id) : undefined;
  }
  if (
    typeof d === "object" &&
    "id" in d &&
    (d as { id?: unknown }).id != null
  ) {
    return Number((d as { id: number }).id);
  }
  return undefined;
}

function mergeNewBookCategoryIdsFromResponse(
  rows: BookFormData["categories"],
  catalog: { id: string | number; code: string }[] | undefined,
  res: BookCategoriesResponse,
): BookFormData["categories"] {
  const raw = res.data;
  let list: BookCategoryItem[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (
    raw &&
    typeof raw === "object" &&
    Array.isArray((raw as { categories?: BookCategoryItem[] }).categories)
  ) {
    list = (raw as { categories: BookCategoryItem[] }).categories;
  }
  if (!list.length || !catalog?.length) return rows;
  return rows.map((row) => {
    if (row.bookCategoryId != null) return row;
    const cat = catalog.find((c) => c.code === row.categoryCode);
    if (!cat) return row;
    const hit = list.find((l) => Number(l.category_id) === Number(cat.id));
    if (hit?.id != null) return { ...row, bookCategoryId: Number(hit.id) };
    return row;
  });
}

export default function BookCreate({
  headerTitle,
  prefill,
  openSection,
}: BookCreateProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    prefill?: Partial<BookFormData>;
  } | null;
  const mergedPrefill = { ...prefill, ...locationState?.prefill };
  const { toast } = useToast();
  const { data: categories, isLoading: categoriesLoading } =
    useCategoriesData();
  const createBookMutation = useCreateBook();
  const updateBookMutation = useUpdateBook();
  const createBookVersionMutation = useCreateBookVersion();
  const updateBookVersionMutation = useUpdateBookVersion();
  const createBookCategoriesMutation = useCreateBookCategories();
  const updateBookCategoryMutation = useUpdateBookCategory();
  const deleteBookCategoryMutation = useDeleteBookCategory();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>("");

  const [formData, setFormData] = useState<BookFormData>(() => ({
    bookId: mergedPrefill?.bookId ?? "",
    bookVersionId: mergedPrefill?.bookVersionId,
    title: mergedPrefill?.title ?? "",
    isbn: mergedPrefill?.isbn ?? "",
    sankhyaCode: mergedPrefill?.sankhyaCode ?? "",
    materialidade: mergedPrefill?.materialidade ?? "",
    cartonadoSubtype: mergedPrefill?.cartonadoSubtype ?? "",
    categories: mergedPrefill?.categories ?? [],
    cost: mergedPrefill?.cost ?? 0,
    contractType: mergedPrefill?.contractType ?? "",
    contractExpiryDate: mergedPrefill?.contractExpiryDate ?? "",
    contractStatus: mergedPrefill?.contractStatus ?? "",
  }));

  const [sectionStatus, setSectionStatus] = useState<SectionStatus>({
    identificacao: false,
    categorias: false,
    versoes: false,
  });

  const [sectionExpanded, setSectionExpanded] = useState<SectionExpanded>(
    () => ({
      identificacao: openSection ? openSection === "identificacao" : true,
      categorias: openSection ? openSection === "categorias" : false,
      versoes: openSection ? openSection === "versoes" : false,
    }),
  );

  const [savedSections, setSavedSections] = useState<SectionStatus>({
    identificacao: false,
    categorias: false,
    versoes: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showSecondaryCategory, setShowSecondaryCategory] = useState(
    () => mergedPrefill?.categories?.some((c) => !c.isMain) || false,
  );
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const closeCartonadoTimer = useRef<number | null>(null);
  const cartonadoItemRef = useRef<HTMLDivElement | null>(null);
  const [cartonadoSide, setCartonadoSide] = useState<"right" | "left">("right");
  const [cartonadoMaxHeight, setCartonadoMaxHeight] = useState<number>(592);

  const computeCartonadoMaxHeight = () => {
    if (!cartonadoItemRef.current) return;
    const rect = cartonadoItemRef.current.getBoundingClientRect();
    const availableBelow = window.innerHeight - rect.top - 16;
    const clamped = Math.max(200, Math.min(592, availableBelow));
    setCartonadoMaxHeight(clamped);
  };

  const startCloseCartonado = () => {
    if (closeCartonadoTimer.current)
      window.clearTimeout(closeCartonadoTimer.current);
    closeCartonadoTimer.current = window.setTimeout(
      () => setOpenDropdown("materialidade"),
      150,
    );
  };

  const cancelCloseCartonado = () => {
    if (closeCartonadoTimer.current) {
      window.clearTimeout(closeCartonadoTimer.current);
      closeCartonadoTimer.current = null;
    }
  };

  const handleCartonadoEnter = () => {
    cancelCloseCartonado();
    setOpenDropdown("materialidade-cartonado");
    if (cartonadoItemRef.current) {
      const rect = cartonadoItemRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      const panelTotal = 280 + 8;
      setCartonadoSide(spaceRight < panelTotal ? "left" : "right");
      const availableBelow = window.innerHeight - rect.top - 16;
      const clamped = Math.max(200, Math.min(592, availableBelow));
      setCartonadoMaxHeight(clamped);
    }
  };

  const isIdentificacaoComplete = () => {
    const baseOk =
      formData.title.trim() !== "" &&
      formData.materialidade.trim() !== "" &&
      formData.cost > 0 &&
      formData.contractType.trim() !== "";
    if (!baseOk) return false;
    if (formData.contractType === "Determinado") {
      return formData.contractExpiryDate.trim() !== "";
    }
    return true;
  };

  const isCategoriasComplete = () => {
    return formData.categories.length > 0;
  };

  const isVersoesComplete = () => {
    return formData.isbn.trim() !== "" && formData.sankhyaCode.trim() !== "";
  };

  useEffect(() => {
    setSectionStatus({
      identificacao: isIdentificacaoComplete(),
      categorias: isCategoriasComplete(),
      versoes: isVersoesComplete(),
    });
  }, [formData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-dropdown-container]")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (openDropdown !== "materialidade-cartonado") return;
    const onResize = () => computeCartonadoMaxHeight();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    computeCartonadoMaxHeight();
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [openDropdown]);

  const handleSaveSection = async (section: keyof SectionStatus) => {
    if (!sectionStatus[section]) return;

    if (section === "identificacao") {
      const materialityId = getMaterialityId(formData.materialidade);
      if (!materialityId) {
        toast({
          variant: "destructive",
          description:
            "Materialidade inválida. Selecione uma materialidade válida.",
        });
        return;
      }

      const contractType =
        formData.contractType === "Determinado"
          ? "determinate"
          : "indeterminate";
      const contractExpiration =
        formData.contractType === "Determinado"
          ? parseBRDate(formData.contractExpiryDate)
          : null;

      const now = new Date();
      const timestamp = `Última atualização hoje às ${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`;

      if (formData.bookId) {
        try {
          await updateBookMutation.mutateAsync({
            id: parseInt(formData.bookId),
            body: {
              title: formData.title.trim(),
              cost: formData.cost,
              materiality_id: materialityId,
              contract_type: contractType,
              contract_expiration: contractExpiration,
            },
          });
          setSavedSections((prev) => ({ ...prev, identificacao: true }));
          setHasUnsavedChanges(false);
          setLastUpdate(timestamp);
          toast({ description: "Livro atualizado com sucesso." });
        } catch (err: unknown) {
          if (!isHandledError(err)) {
            const msg =
              (err as { message?: string })?.message ??
              "Erro ao atualizar livro.";
            toast({ variant: "destructive", description: msg });
          }
        }
      } else {
        try {
          const response = await createBookMutation.mutateAsync({
            title: formData.title.trim(),
            cost: formData.cost,
            materiality_id: materialityId,
            status: "inactive",
            contract_type: contractType,
            contract_expiration: contractExpiration,
          });
          setFormData((prev) => ({
            ...prev,
            bookId: String(response.data.id),
          }));
          setSavedSections((prev) => ({ ...prev, identificacao: true }));
          setHasUnsavedChanges(false);
          setLastUpdate(timestamp);
          toast({
            description:
              "Livro criado com sucesso. Continue com ERP/ISBN e Categorias.",
          });
          navigate(`/catalog/${response.data.id}/editar?section=versoes`);
        } catch (err: unknown) {
          if (!isHandledError(err)) {
            const msg =
              (err as { message?: string })?.message ?? "Erro ao criar livro.";
            toast({ variant: "destructive", description: msg });
          }
        }
      }
      return;
    }

    const bookId = parseInt(formData.bookId, 10);
    if (!bookId || Number.isNaN(bookId)) {
      toast({
        variant: "destructive",
        description: "Salve a seção de identificação antes de continuar.",
      });
      return;
    }

    if (section === "versoes") {
      setIsSaving(true);
      try {
        const vid = formData.bookVersionId;
        if (vid != null && vid > 0) {
          await updateBookVersionMutation.mutateAsync({
            versionId: vid,
            bookId,
            body: {
              erp_code: formData.sankhyaCode,
              isbn: formData.isbn,
            },
          });
        } else {
          const res = await createBookVersionMutation.mutateAsync({
            book_id: bookId,
            erp_code: formData.sankhyaCode,
            isbn: formData.isbn,
          });
          const newId = extractBookVersionIdFromResponse(res);
          if (newId != null) {
            setFormData((prev) => ({ ...prev, bookVersionId: newId }));
          }
        }
        setSavedSections((prev) => ({ ...prev, versoes: true }));
        setHasUnsavedChanges(false);
        const now = new Date();
        setLastUpdate(
          `Última atualização hoje às ${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`,
        );
        toast({ description: "Versão (ERP/ISBN) salva com sucesso." });
      } catch (err: unknown) {
        if (!isHandledError(err)) {
          const msg =
            (err as { message?: string })?.message ??
            "Erro ao salvar versão do livro.";
          toast({ variant: "destructive", description: msg });
        }
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (section === "categorias") {
      setIsSaving(true);
      try {
        type RowPayload = {
          category_id: number;
          csat: number | null;
          is_new: boolean;
          is_main: boolean;
          bookCategoryId?: number;
        };
        const rowPayloads: RowPayload[] = formData.categories
          .map((cat): RowPayload | null => {
            const match = categories?.find((c) => c.code === cat.categoryCode);
            if (!match) return null;
            return {
              category_id: Number(match.id),
              csat: cat.withoutNote ? null : Number(cat.csat),
              is_new: !!cat.withoutNote,
              is_main: !!cat.isMain,
              bookCategoryId: cat.bookCategoryId,
            };
          })
          .filter((c): c is RowPayload => c !== null)
          .filter((c) => Number.isFinite(c.csat as number) || c.is_new);

        if (!rowPayloads.length) {
          toast({
            variant: "destructive",
            description:
              "Selecione categorias válidas e preencha o CSAT quando obrigatório.",
          });
          setIsSaving(false);
          return;
        }

        const toUpdate = rowPayloads.filter(
          (r) => r.bookCategoryId != null && r.bookCategoryId > 0,
        );
        const toCreate = rowPayloads.filter(
          (r) => r.bookCategoryId == null || r.bookCategoryId <= 0,
        );

        for (const r of toUpdate) {
          await updateBookCategoryMutation.mutateAsync({
            bookCategoryId: r.bookCategoryId!,
            bookId,
            body: {
              csat: r.csat,
              is_new: r.is_new,
              is_main: r.is_main,
            },
          });
        }

        if (toCreate.length > 0) {
          const res = await createBookCategoriesMutation.mutateAsync({
            book_id: bookId,
            categories: toCreate.map(
              ({ category_id, csat, is_new, is_main }) => ({
                category_id,
                csat,
                is_new,
                is_main,
              }),
            ),
          });
          setFormData((prev) => ({
            ...prev,
            categories: mergeNewBookCategoryIdsFromResponse(
              prev.categories,
              categories,
              res,
            ),
          }));
        }

        setSavedSections((prev) => ({ ...prev, categorias: true }));
        setHasUnsavedChanges(false);
        const now = new Date();
        setLastUpdate(
          `Última atualização hoje às ${now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`,
        );
        toast({ description: "Categorias e CSAT salvos com sucesso." });
      } catch (err: unknown) {
        if (!isHandledError(err)) {
          const msg =
            (err as { message?: string })?.message ??
            "Erro ao salvar categorias do livro.";
          toast({ variant: "destructive", description: msg });
        }
      } finally {
        setIsSaving(false);
      }
      return;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof BookFormData,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSelectChange = (field: keyof BookFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleRemoveBookCategory = async (
    cat: BookFormData["categories"][number],
  ) => {
    const bookId = parseInt(formData.bookId, 10);
    const removeLocal = () => {
      setFormData((prev) => ({
        ...prev,
        categories: prev.categories.filter((c) => c.id !== cat.id),
      }));
      setHasUnsavedChanges(true);
    };

    if (!cat.bookCategoryId || Number.isNaN(bookId) || bookId <= 0) {
      removeLocal();
      return;
    }

    try {
      await deleteBookCategoryMutation.mutateAsync({
        bookCategoryId: cat.bookCategoryId,
        bookId,
      });
      removeLocal();
      toast({ description: "Categoria removida do livro." });
    } catch (err: unknown) {
      if (!isHandledError(err)) {
        const msg =
          (err as { message?: string })?.message ?? "Erro ao remover categoria.";
        toast({ variant: "destructive", description: msg });
      }
    }
  };

  const toggleSection = (section: keyof SectionExpanded) => {
    setSectionExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      navigate("/catalog");
    }
  };

  const handleConfirmExit = () => {
    navigate("/catalog");
  };

  const formatDateBR = (value: string): string => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4)
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateBR(e.target.value);
    setFormData((prev) => ({
      ...prev,
      contractExpiryDate: formatted,
    }));
    setHasUnsavedChanges(true);
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />

      <div className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center h-[69px] px-6 border-b border-[#BAC9CA]">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNavigateBack}
            className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
          >
            <ArrowLeft className="w-6 h-6 text-[#3B494A]" />
          </Button>
          <h1 className="flex-1 text-[22px] leading-7 text-[#151C1D]">
            {headerTitle ?? "Cadastrar Novo Livro"}
          </h1>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-white">
          <div className="max-w-full py-9 px-6">
            {lastUpdate && (
              <div className="flex justify-end mb-6">
                <span className="text-xs font-medium text-[#3B494A]">
                  {lastUpdate}
                </span>
              </div>
            )}

            {/* Form Sections */}
            <div className="flex flex-col gap-5">
              {/* ERP/ISBN - FIRST */}
              <div
                className={`self-stretch rounded-[4px] border border-[#BAC9CA] bg-white overflow-hidden ${
                  formData.bookId ? "" : "hidden"
                }`}
              >
                <div
                  className="flex justify-between items-center px-6 py-4 bg-[#EDF4F5] border-b border-[#BAC9CA] cursor-pointer hover:bg-[#E8EFF0]"
                  onClick={() => toggleSection("versoes")}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      ERP/ISBN
                    </h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 hover:bg-[#0000001A]"
                  >
                    {sectionExpanded.versoes ? (
                      <ChevronUp className="w-5 h-5 text-[#3B494A]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#3B494A]" />
                    )}
                  </Button>
                </div>

                {sectionExpanded.versoes && (
                  <>
                    <div className="px-6 pt-5 pb-0 bg-white">
                      <h3 className="text-xs font-medium text-[#151C1D] mb-5">
                        Dados do Sistema
                      </h3>

                      <div className="space-y-5 mb-5">
                        <div>
                          <FloatingInput
                            label="Código Sankhya *"
                            value={formData.sankhyaCode}
                            onChange={(e) =>
                              handleInputChange(e, "sankhyaCode")
                            }
                          />
                        </div>
                        <div>
                          <FloatingInput
                            label="ISBN *"
                            value={formData.isbn}
                            onChange={(e) => handleInputChange(e, "isbn")}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end px-4 py-2 bg-[#EDF4F5] border-t border-[#BAC9CA]">
                      <Button
                        onClick={() => handleSaveSection("versoes")}
                        disabled={
                          !sectionStatus.versoes ||
                          isSaving ||
                          createBookVersionMutation.isPending ||
                          updateBookVersionMutation.isPending
                        }
                        className="h-12 px-4 bg-[#00646B] hover:bg-[#00646B]/90 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:bg-[#0000001A] disabled:text-[#151C1D]"
                      >
                        {isSaving ? "Salvando..." : "Salvar"}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Identificação do Livro - SECOND */}
              <div className="self-stretch rounded-[4px] border border-[#BAC9CA] bg-white">
                <div
                  className="flex justify-between items-center px-6 py-4 bg-[#EDF4F5] border-b border-[#BAC9CA] cursor-pointer hover:bg-[#E8EFF0]"
                  onClick={() => toggleSection("identificacao")}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      Identificação do Livro
                    </h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 hover:bg-[#0000001A]"
                  >
                    {sectionExpanded.identificacao ? (
                      <ChevronUp className="w-5 h-5 text-[#3B494A]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#3B494A]" />
                    )}
                  </Button>
                </div>

                {sectionExpanded.identificacao && (
                  <>
                    <div className="px-6 pt-5 pb-0 bg-white relative">
                      <h3 className="text-xs font-medium text-[#151C1D] mb-5">
                        Dados Básicos
                      </h3>
                      <div className="space-y-5 mb-5">
                        <div>
                          <FloatingInput
                            label="Título *"
                            value={formData.title}
                            onChange={(e) => handleInputChange(e, "title")}
                          />
                        </div>
                        <div>
                          <FloatingInput
                            type="number"
                            label="Custo (R$) *"
                            value={formData.cost || ""}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                cost: parseFloat(e.target.value) || 0,
                              }));
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>

                        <div data-dropdown-container className="relative">
                          <label className="text-xs font-medium text-[#3B494A] block mb-2">
                            Materialidade *
                          </label>
                          <button
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === "materialidade"
                                  ? null
                                  : "materialidade",
                              )
                            }
                            className={`h-14 w-full rounded bg-white text-[#151C1D] text-base text-left px-4 flex items-center justify-between transition-colors ${
                              openDropdown === "materialidade" ||
                              openDropdown === "materialidade-cartonado"
                                ? "border-[3px] border-[#00646B]"
                                : "border border-[#6B797A] hover:border-[#6B797A]"
                            }`}
                          >
                            <span
                              className={
                                formData.materialidade ? "" : "text-[#3B494A]"
                              }
                            >
                              {formData.materialidade ||
                                "Selecione uma materialidade"}
                            </span>
                            <ChevronDown
                              className={`w-6 h-6 text-[#3B494A] transition-transform ${
                                openDropdown === "materialidade" ||
                                openDropdown === "materialidade-cartonado"
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </button>

                          {(openDropdown === "materialidade" ||
                            openDropdown === "materialidade-cartonado") && (
                            <div className="absolute top-full left-0 mt-2 w-full bg-[#E8EFF0] rounded shadow-[0_1px_2px_0_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)] z-50 py-2 max-h-80 overflow-y-auto">
                              {MATERIALITIES.map((mat) => (
                                <div
                                  key={mat.id}
                                  className="h-12 px-3 text-base text-[#151C1D] flex items-center cursor-pointer hover:bg-[#0000001A] transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectChange(
                                      "materialidade",
                                      mat.name,
                                    );
                                    handleSelectChange("cartonadoSubtype", "");
                                    setOpenDropdown(null);
                                  }}
                                >
                                  {mat.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="text-xs font-medium text-[#3B494A] block mb-2">
                            Tipo de Contrato *
                          </label>
                          <div className="flex gap-4">
                            <button
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  contractType: "Determinado",
                                }));
                                setHasUnsavedChanges(true);
                              }}
                              className="flex items-center gap-2 h-10 px-4 rounded border transition-colors"
                            >
                              <div
                                className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                                style={{
                                  borderColor:
                                    formData.contractType === "Determinado"
                                      ? "#00646B"
                                      : "#6B797A",
                                  backgroundColor:
                                    formData.contractType === "Determinado"
                                      ? "#00646B"
                                      : "transparent",
                                }}
                              >
                                {formData.contractType === "Determinado" && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm text-[#151C1D]">
                                Determinado
                              </span>
                            </button>
                            <button
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  contractType: "Indeterminado",
                                }));
                                setHasUnsavedChanges(true);
                              }}
                              className="flex items-center gap-2 h-10 px-4 rounded border transition-colors"
                            >
                              <div
                                className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                                style={{
                                  borderColor:
                                    formData.contractType === "Indeterminado"
                                      ? "#00646B"
                                      : "#6B797A",
                                  backgroundColor:
                                    formData.contractType === "Indeterminado"
                                      ? "#00646B"
                                      : "transparent",
                                }}
                              >
                                {formData.contractType === "Indeterminado" && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="text-sm text-[#151C1D]">
                                Indeterminado
                              </span>
                            </button>
                          </div>
                        </div>

                        <div>
                          <FloatingInput
                            type="text"
                            label="Data de Expiração do Contrato *"
                            value={formData.contractExpiryDate}
                            onChange={handleDateChange}
                            placeholder="DD/MM/AAAA"
                            maxLength={10}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end px-4 py-2 bg-[#EDF4F5] border-t border-[#BAC9CA]">
                      <Button
                        onClick={() => handleSaveSection("identificacao")}
                        disabled={
                          !sectionStatus.identificacao ||
                          createBookMutation.isPending ||
                          updateBookMutation.isPending
                        }
                        className="h-12 px-4 bg-[#00646B] hover:bg-[#00646B]/90 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:bg-[#0000001A] disabled:text-[#151C1D]"
                      >
                        {createBookMutation.isPending ||
                        updateBookMutation.isPending
                          ? "Salvando..."
                          : "Salvar"}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Categorias - THIRD */}
              <div
                className={`self-stretch rounded-[4px] border border-[#BAC9CA] bg-white overflow-hidden ${
                  formData.bookId ? "" : "hidden"
                }`}
              >
                <div
                  className="flex justify-between items-center px-6 py-4 bg-[#EDF4F5] border-b border-[#BAC9CA] cursor-pointer hover:bg-[#E8EFF0]"
                  onClick={() => toggleSection("categorias")}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      Categorias e CSAT
                    </h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 hover:bg-[#0000001A]"
                  >
                    {sectionExpanded.categorias ? (
                      <ChevronUp className="w-5 h-5 text-[#3B494A]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#3B494A]" />
                    )}
                  </Button>
                </div>

                {sectionExpanded.categorias && (
                  <>
                    <div className="px-6 pt-5 pb-0 bg-white">
                      <h3 className="text-xs font-medium text-[#151C1D] mb-5">
                        Categorias e CSAT
                      </h3>

                      {/* Primary Category Section */}
                      <div className="mb-5 border border-[#BAC9CA] rounded-lg overflow-hidden">
                        <div className="h-14 px-4 flex items-center gap-2 bg-[#D6E5E6]">
                          <div className="w-6 h-6 rounded-full bg-[#3B494A]/20 flex items-center justify-center">
                            <svg
                              className="w-5 h-5 fill-[#3B494A] opacity-80"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.607 19.2C8.28567 19.2 7.04167 18.95 5.875 18.45C4.70833 17.95 3.6875 17.2625 2.8125 16.3875C1.9375 15.5125 1.25 14.492 0.75 13.326C0.25 12.16 0 10.9142 0 9.5885C0 8.26283 0.25 7.02083 0.75 5.8625C1.25 4.70417 1.9375 3.6875 2.8125 2.8125C3.6875 1.9375 4.708 1.25 5.874 0.75C7.04 0.25 8.28583 0 9.6115 0C10.9372 0 12.1792 0.25 13.3375 0.75C14.4958 1.25 15.5125 1.9375 16.3875 2.8125C17.2625 3.6875 17.95 4.706 18.45 5.868C18.95 7.03017 19.2 8.27183 19.2 9.593C19.2 10.9143 18.95 12.1583 18.45 13.325C17.95 14.4917 17.2625 15.5125 16.3875 16.3875C15.5125 17.2625 14.494 17.95 13.332 18.45C12.1698 18.95 10.9282 19.2 9.607 19.2ZM9.6 6.6V13.5C9.6 13.755 9.68575 13.9688 9.85725 14.1413C10.0288 14.3138 10.2413 14.4 10.4948 14.4C10.7483 14.4 10.9625 14.3138 11.1375 14.1413C11.3125 13.9688 11.4 13.755 11.4 13.5V5.7C11.4 5.445 11.3138 5.23125 11.1413 5.05875C10.9688 4.88625 10.755 4.8 10.5 4.8H8.1C7.845 4.8 7.63125 4.88575 7.45875 5.05725C7.28625 5.22875 7.2 5.44125 7.2 5.69475C7.2 5.94825 7.28625 6.1625 7.45875 6.3375C7.63125 6.5125 7.845 6.6 8.1 6.6H9.6Z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-[#151C1D]">
                            Categoria Primária
                          </span>
                        </div>

                        <div className="p-5 space-y-3">
                          {/* Category Chips */}
                          <div className="flex flex-wrap gap-2 p-2">
                            {categoriesLoading ? (
                              <div className="text-sm text-gray-500">
                                Carregando categorias...
                              </div>
                            ) : (
                              categories?.map((category) => {
                                const code = category.code;
                                const exists = formData.categories.find(
                                  (c) => c.categoryCode === code && c.isMain,
                                );
                                return (
                                  <button
                                    key={code}
                                    onClick={() => {
                                      if (exists) {
                                        // Remove from primary (and keep only explicit secondary, not auto-included)
                                        setFormData((prev) => ({
                                          ...prev,
                                          categories: prev.categories.filter(
                                            (c) =>
                                              !(
                                                c.categoryCode === code &&
                                                c.isMain
                                              ),
                                          ),
                                        }));
                                      } else {
                                        // Add to primary (will auto-appear in secondary)
                                        setFormData((prev) => ({
                                          ...prev,
                                          categories: [
                                            ...prev.categories,
                                            {
                                              id: `cat-${code}-${Date.now()}`,
                                              categoryCode: code,
                                              csat: "",
                                              withoutNote: false,
                                              isMain: true,
                                            },
                                          ],
                                        }));
                                      }
                                      setHasUnsavedChanges(true);
                                    }}
                                    className={`h-8 flex items-center gap-2 px-4 rounded-lg transition-colors text-sm font-medium ${
                                      exists
                                        ? "bg-[#B4EEDF] text-[#366E62] border-0"
                                        : "border border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#F5F5F5]"
                                    }`}
                                  >
                                    {exists && (
                                      <Check className="w-[18px] h-[18px]" />
                                    )}
                                    {code}
                                  </button>
                                );
                              })
                            )}
                          </div>

                          {/* Configurations Section */}
                          {formData.categories.filter((c) => c.isMain).length >
                          0 ? (
                            <>
                              <div className="pt-3">
                                <h4 className="text-xs font-medium text-[#151C1D] mb-3">
                                  Configurações
                                </h4>
                              </div>
                              <div className="flex flex-wrap gap-5">
                                {formData.categories
                                  .filter((c) => c.isMain)
                                  .map((cat) => (
                                    <div
                                      key={cat.id}
                                      className="min-w-[338px] flex-1 rounded-lg bg-[#EDF4F5] shadow-[0_1px_2px_0_rgba(0,0,0,0.3),0_1px_3px_1px_rgba(0,0,0,0.15)]"
                                    >
                                      {/* Avatar Header */}
                                      <div className="pt-4 px-6 flex items-center justify-between gap-2">
                                        <div className="w-10 h-10 rounded-full bg-[#00CBD9] flex items-center justify-center">
                                          <span className="text-base font-medium text-[#005359]">
                                            {cat.categoryCode}
                                          </span>
                                        </div>
                                        {formData.bookId ? (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 shrink-0 text-[#93000A] hover:text-[#93000A] hover:bg-[#FFDAD6]/50"
                                            onClick={() =>
                                              void handleRemoveBookCategory(cat)
                                            }
                                            disabled={
                                              deleteBookCategoryMutation.isPending
                                            }
                                            aria-label="Remover categoria"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        ) : null}
                                      </div>

                                      {/* CSAT Input */}
                                      <div className="px-6 pt-5 space-y-2">
                                        <div className="relative">
                                          <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="5"
                                            disabled={cat.withoutNote}
                                            value={
                                              cat.withoutNote ? "" : cat.csat
                                            }
                                            onChange={(e) => {
                                              setFormData((prev) => ({
                                                ...prev,
                                                categories: prev.categories.map(
                                                  (c) =>
                                                    c.id === cat.id
                                                      ? {
                                                          ...c,
                                                          csat: e.target.value,
                                                        }
                                                      : c,
                                                ),
                                              }));
                                              setHasUnsavedChanges(true);
                                            }}
                                            className="h-14 w-full bg-[#DDE4E4] rounded-t-lg px-4 text-base text-[#3B494A] border-0 focus:outline-none disabled:opacity-50"
                                            placeholder="CSAT"
                                          />
                                          <div className="h-px bg-[#3F4849]"></div>
                                        </div>

                                        {/* Checkbox */}
                                        <div className="h-10 flex items-center pl-3 pr-6">
                                          <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={cat.withoutNote}
                                              onChange={(e) => {
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  categories:
                                                    prev.categories.map((c) =>
                                                      c.id === cat.id
                                                        ? {
                                                            ...c,
                                                            withoutNote:
                                                              e.target.checked,
                                                            csat: e.target
                                                              .checked
                                                              ? ""
                                                              : c.csat,
                                                          }
                                                        : c,
                                                    ),
                                                }));
                                                setHasUnsavedChanges(true);
                                              }}
                                              className="w-[18px] h-[18px] rounded-sm border-2 border-[#3B494A] text-[#00646B] focus:ring-0 focus:ring-offset-0"
                                            />
                                            <span className="text-sm text-[#3B494A]">
                                              Categoria sem nota
                                            </span>
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </>
                          ) : (
                            <div className="p-6 border border-dashed border-[#6B797A] rounded-lg bg-[#EDF4F5]">
                              <p className="text-xs text-[#3B494A] text-center">
                                Selecione uma ou mais categorias acima para
                                configurá-las
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Add Secondary Category Button / Section */}
                      {!showSecondaryCategory ? (
                        <div className="flex justify-center mb-5">
                          <Button
                            variant="ghost"
                            className="h-12 gap-1 text-[#00646B] hover:bg-transparent"
                            onClick={() => setShowSecondaryCategory(true)}
                          >
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 12 12"
                              fill="currentColor"
                            >
                              <path d="M5 6.66667H0.833333C0.597222 6.66667 0.399306 6.58681 0.239583 6.42708C0.0798611 6.26736 0 6.06944 0 5.83333C0 5.59722 0.0798611 5.39931 0.239583 5.23958C0.399306 5.07986 0.597222 5 0.833333 5H5V0.833333C5 0.597222 5.07986 0.399306 5.23958 0.239583C5.39931 0.0798611 5.59722 0 5.83333 0C6.06944 0 6.26736 0.0798611 6.42708 0.239583C6.58681 0.399306 6.66667 0.597222 6.66667 0.833333V5H10.8333C11.0694 5 11.2674 5.07986 11.4271 5.23958C11.5868 5.39931 11.6667 5.59722 11.6667 5.83333C11.6667 6.06944 11.5868 6.26736 11.4271 6.42708C11.2674 6.58681 11.0694 6.66667 10.8333 6.66667H6.66667V10.8333C6.66667 11.0694 6.58681 11.2674 6.42708 11.4271C6.26736 11.5868 6.06944 11.6667 5.83333 11.6667C5.59722 11.6667 5.39931 11.5868 5.23958 11.4271C5.07986 11.2674 5 11.0694 5 10.8333V6.66667Z" />
                            </svg>
                            <span className="text-sm font-medium">
                              Categoria Secundária
                            </span>
                          </Button>
                        </div>
                      ) : (
                        <div className="mb-5 border border-[#BAC9CA] rounded-lg overflow-hidden">
                          <div className="h-14 px-4 flex items-center gap-2 bg-[#EDF4F5]">
                            <div className="w-6 h-6 rounded-full bg-[#3B494A]/20 flex items-center justify-center">
                              <span className="text-sm font-medium text-[#3B494A]">
                                2
                              </span>
                            </div>
                            <span className="text-sm font-medium text-[#151C1D]">
                              Categoria Secundária
                            </span>
                          </div>

                          <div className="p-5 space-y-3">
                            {/* Category Chips */}
                            <div className="flex flex-wrap gap-2 p-2">
                              {categoriesLoading ? (
                                <div className="text-sm text-gray-500">
                                  Carregando categorias...
                                </div>
                              ) : (
                                categories?.map((category) => {
                                  const code = category.code;
                                  const isPrimary = formData.categories.find(
                                    (c) => c.categoryCode === code && c.isMain,
                                  );
                                  const isSecondary = formData.categories.find(
                                    (c) => c.categoryCode === code && !c.isMain,
                                  );
                                  const isLocked = !!isPrimary;

                                  return (
                                    <button
                                      key={code}
                                      onClick={() => {
                                        if (isLocked) return; // Cannot deselect if it's a primary category

                                        if (isSecondary) {
                                          setFormData((prev) => ({
                                            ...prev,
                                            categories: prev.categories.filter(
                                              (c) =>
                                                !(
                                                  c.categoryCode === code &&
                                                  !c.isMain
                                                ),
                                            ),
                                          }));
                                        } else {
                                          setFormData((prev) => ({
                                            ...prev,
                                            categories: [
                                              ...prev.categories,
                                              {
                                                id: `cat-sec-${code}-${Date.now()}`,
                                                categoryCode: code,
                                                csat: "",
                                                withoutNote: false,
                                                isMain: false,
                                              },
                                            ],
                                          }));
                                        }
                                        setHasUnsavedChanges(true);
                                      }}
                                      disabled={isLocked}
                                      className={`h-8 flex items-center gap-2 px-4 rounded-lg transition-colors text-sm font-medium ${
                                        isPrimary || isSecondary
                                          ? "bg-[#B4EEDF] text-[#366E62] border-0"
                                          : "border border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#F5F5F5]"
                                      } ${isLocked ? "opacity-100 cursor-not-allowed" : ""}`}
                                    >
                                      {(isPrimary || isSecondary) && (
                                        <Check className="w-[18px] h-[18px]" />
                                      )}
                                      {code}
                                    </button>
                                  );
                                })
                              )}
                            </div>

                            {/* Info text */}
                            <div className="text-xs text-[#3B494A] italic">
                              * Categorias primárias são automaticamente
                              incluídas como secundárias
                            </div>

                            {/* Configurations Section for Secondary */}
                            {formData.categories.filter((c) => !c.isMain)
                              .length > 0 && (
                              <>
                                <div className="pt-3">
                                  <h4 className="text-xs font-medium text-[#151C1D] mb-3">
                                    Configurações
                                  </h4>
                                </div>
                                <div className="flex flex-wrap gap-5">
                                  {formData.categories
                                    .filter((c) => !c.isMain)
                                    .map((cat) => (
                                      <div
                                        key={cat.id}
                                        className="min-w-[338px] flex-1 rounded-lg bg-[#EDF4F5] shadow-[0_1px_2px_0_rgba(0,0,0,0.3),0_1px_3px_1px_rgba(0,0,0,0.15)]"
                                      >
                                        {/* Avatar Header */}
                                        <div className="pt-4 px-6 flex items-center justify-between gap-2">
                                          <div className="w-10 h-10 rounded-full bg-[#00CBD9] flex items-center justify-center">
                                            <span className="text-base font-medium text-[#005359]">
                                              {cat.categoryCode}
                                            </span>
                                          </div>
                                          {formData.bookId ? (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-9 w-9 shrink-0 text-[#93000A] hover:text-[#93000A] hover:bg-[#FFDAD6]/50"
                                              onClick={() =>
                                                void handleRemoveBookCategory(
                                                  cat,
                                                )
                                              }
                                              disabled={
                                                deleteBookCategoryMutation.isPending
                                              }
                                              aria-label="Remover categoria"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          ) : null}
                                        </div>

                                        {/* CSAT Input */}
                                        <div className="px-6 pt-5 space-y-2">
                                          <div className="relative">
                                            <input
                                              type="number"
                                              step="0.1"
                                              min="0"
                                              max="5"
                                              disabled={cat.withoutNote}
                                              value={
                                                cat.withoutNote ? "" : cat.csat
                                              }
                                              onChange={(e) => {
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  categories:
                                                    prev.categories.map((c) =>
                                                      c.id === cat.id
                                                        ? {
                                                            ...c,
                                                            csat: e.target
                                                              .value,
                                                          }
                                                        : c,
                                                    ),
                                                }));
                                                setHasUnsavedChanges(true);
                                              }}
                                              className="h-14 w-full bg-[#DDE4E4] rounded-t-lg px-4 text-base text-[#3B494A] border-0 focus:outline-none disabled:opacity-50"
                                              placeholder="CSAT"
                                            />
                                            <div className="h-px bg-[#3F4849]"></div>
                                          </div>

                                          {/* Checkbox */}
                                          <div className="h-10 flex items-center pl-3 pr-6">
                                            <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={cat.withoutNote}
                                                onChange={(e) => {
                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    categories:
                                                      prev.categories.map(
                                                        (c) =>
                                                          c.id === cat.id
                                                            ? {
                                                                ...c,
                                                                withoutNote:
                                                                  e.target
                                                                    .checked,
                                                                csat: e.target
                                                                  .checked
                                                                  ? ""
                                                                  : c.csat,
                                                              }
                                                            : c,
                                                      ),
                                                  }));
                                                  setHasUnsavedChanges(true);
                                                }}
                                                className="w-[18px] h-[18px] rounded-sm border-2 border-[#3B494A] text-[#00646B] focus:ring-0 focus:ring-offset-0"
                                              />
                                              <span className="text-sm text-[#3B494A]">
                                                Categoria sem nota
                                              </span>
                                            </label>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end px-4 py-2 bg-[#EDF4F5] border-t border-[#BAC9CA]">
                      <Button
                        onClick={() => handleSaveSection("categorias")}
                        disabled={
                          !sectionStatus.categorias ||
                          isSaving ||
                          createBookCategoriesMutation.isPending ||
                          updateBookCategoryMutation.isPending ||
                          deleteBookCategoryMutation.isPending
                        }
                        className="h-12 px-4 bg-[#00646B] hover:bg-[#00646B]/90 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:bg-[#0000001A] disabled:text-[#151C1D]"
                      >
                        {isSaving ? "Salvando..." : "Salvar"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
          <AlertDialogDescription>
            Os dados informados não serão salvos.
          </AlertDialogDescription>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel>Ficar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExit}
              className="bg-[#BA1A1A] hover:bg-[#A01010] text-white"
            >
              Sair sem salvar
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
